/**
 * AdminContext - Centralized state management for Backoffice Admin
 * Stores admin preferences, filters, pagination, and UI state
 * Only used in admin routes (/admin/*)
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface AdminContextType {
  // Dashboard state
  dashboardRefreshInterval: number;
  setDashboardRefreshInterval: (interval: number) => void;

  // Users management
  usersPageSize: number;
  setUsersPageSize: (size: number) => void;
  usersSearchQuery: string;
  setUsersSearchQuery: (query: string) => void;
  usersCurrentPage: number;
  setUsersCurrentPage: (page: number) => void;
  usersSortBy: 'name' | 'email' | 'createdAt' | 'balance';
  setUsersSortBy: (sortBy: 'name' | 'email' | 'createdAt' | 'balance') => void;

  // Orders/Transactions management
  ordersPageSize: number;
  setOrdersPageSize: (size: number) => void;
  ordersSearchQuery: string;
  setOrdersSearchQuery: (query: string) => void;
  ordersCurrentPage: number;
  setOrdersCurrentPage: (page: number) => void;
  ordersStatusFilter: 'all' | 'pending' | 'completed' | 'failed';
  setOrdersStatusFilter: (status: 'all' | 'pending' | 'completed' | 'failed') => void;
  ordersSortBy: 'date' | 'amount' | 'status';
  setOrdersSortBy: (sortBy: 'date' | 'amount' | 'status') => void;

  // Audit logs
  auditLogsPageSize: number;
  setAuditLogsPageSize: (size: number) => void;
  auditLogsSearchQuery: string;
  setAuditLogsSearchQuery: (query: string) => void;
  auditLogsCurrentPage: number;
  setAuditLogsCurrentPage: (page: number) => void;
  auditLogsActionFilter: string;
  setAuditLogsActionFilter: (action: string) => void;
  auditLogsSortBy: 'date' | 'action' | 'user';
  setAuditLogsSortBy: (sortBy: 'date' | 'action' | 'user') => void;

  // Profit Dashboard
  profitDateRange: { from: Date; to: Date } | null;
  setProfitDateRange: (range: { from: Date; to: Date } | null) => void;
  profitGroupBy: 'daily' | 'weekly' | 'monthly';
  setProfitGroupBy: (groupBy: 'daily' | 'weekly' | 'monthly') => void;

  // PEAK Export
  peakExportFrequency: 'daily' | 'weekly' | 'monthly';
  setPeakExportFrequency: (frequency: 'daily' | 'weekly' | 'monthly') => void;
  peakExportAutoSync: boolean;
  setPeakExportAutoSync: (enabled: boolean) => void;

  // Profit Alerts
  profitAlertThreshold: number;
  setProfitAlertThreshold: (threshold: number) => void;
  profitAlertEnabled: boolean;
  setProfitAlertEnabled: (enabled: boolean) => void;

  // UI state
  selectedUserId: string | null;
  setSelectedUserId: (id: string | null) => void;
  selectedOrderId: string | null;
  setSelectedOrderId: (id: string | null) => void;
  expandedSections: Record<string, boolean>;
  toggleSection: (sectionId: string) => void;

  // Modal/Dialog state
  isUserDetailModalOpen: boolean;
  setIsUserDetailModalOpen: (open: boolean) => void;
  isOrderDetailModalOpen: boolean;
  setIsOrderDetailModalOpen: (open: boolean) => void;
  isAuditDetailModalOpen: boolean;
  setIsAuditDetailModalOpen: (open: boolean) => void;

  // Notifications
  showNotifications: boolean;
  setShowNotifications: (show: boolean) => void;
  notificationSound: boolean;
  setNotificationSound: (enabled: boolean) => void;

  // Reset all state to defaults
  resetAllState: () => void;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

interface AdminProviderProps {
  children: ReactNode;
}

const defaultState = {
  dashboardRefreshInterval: 30000, // 30 seconds
  usersPageSize: 10,
  usersSearchQuery: '',
  usersCurrentPage: 1,
  usersSortBy: 'createdAt' as const,
  ordersPageSize: 10,
  ordersSearchQuery: '',
  ordersCurrentPage: 1,
  ordersStatusFilter: 'all' as const,
  ordersSortBy: 'date' as const,
  auditLogsPageSize: 10,
  auditLogsSearchQuery: '',
  auditLogsCurrentPage: 1,
  auditLogsActionFilter: '',
  auditLogsSortBy: 'date' as const,
  profitDateRange: null,
  profitGroupBy: 'daily' as const,
  peakExportFrequency: 'daily' as const,
  peakExportAutoSync: true,
  profitAlertThreshold: 1000,
  profitAlertEnabled: true,
  selectedUserId: null,
  selectedOrderId: null,
  expandedSections: {},
  isUserDetailModalOpen: false,
  isOrderDetailModalOpen: false,
  isAuditDetailModalOpen: false,
  showNotifications: true,
  notificationSound: true,
};

export function AdminProvider({ children }: AdminProviderProps) {
  // Dashboard
  const [dashboardRefreshInterval, setDashboardRefreshInterval] = useState(defaultState.dashboardRefreshInterval);

  // Users
  const [usersPageSize, setUsersPageSize] = useState(defaultState.usersPageSize);
  const [usersSearchQuery, setUsersSearchQuery] = useState(defaultState.usersSearchQuery);
  const [usersCurrentPage, setUsersCurrentPage] = useState(defaultState.usersCurrentPage);
  const [usersSortBy, setUsersSortBy] = useState<'name' | 'email' | 'createdAt' | 'balance'>(defaultState.usersSortBy);

  // Orders
  const [ordersPageSize, setOrdersPageSize] = useState(defaultState.ordersPageSize);
  const [ordersSearchQuery, setOrdersSearchQuery] = useState(defaultState.ordersSearchQuery);
  const [ordersCurrentPage, setOrdersCurrentPage] = useState(defaultState.ordersCurrentPage);
  const [ordersStatusFilter, setOrdersStatusFilter] = useState<'all' | 'pending' | 'completed' | 'failed'>(
    defaultState.ordersStatusFilter
  );
  const [ordersSortBy, setOrdersSortBy] = useState<'date' | 'amount' | 'status'>(defaultState.ordersSortBy);

  // Audit Logs
  const [auditLogsPageSize, setAuditLogsPageSize] = useState(defaultState.auditLogsPageSize);
  const [auditLogsSearchQuery, setAuditLogsSearchQuery] = useState(defaultState.auditLogsSearchQuery);
  const [auditLogsCurrentPage, setAuditLogsCurrentPage] = useState(defaultState.auditLogsCurrentPage);
  const [auditLogsActionFilter, setAuditLogsActionFilter] = useState(defaultState.auditLogsActionFilter);
  const [auditLogsSortBy, setAuditLogsSortBy] = useState<'date' | 'action' | 'user'>(defaultState.auditLogsSortBy);

  // Profit Dashboard
  const [profitDateRange, setProfitDateRange] = useState<{ from: Date; to: Date } | null>(defaultState.profitDateRange);
  const [profitGroupBy, setProfitGroupBy] = useState<'daily' | 'weekly' | 'monthly'>(defaultState.profitGroupBy);

  // PEAK Export
  const [peakExportFrequency, setPeakExportFrequency] = useState<'daily' | 'weekly' | 'monthly'>(
    defaultState.peakExportFrequency
  );
  const [peakExportAutoSync, setPeakExportAutoSync] = useState(defaultState.peakExportAutoSync);

  // Profit Alerts
  const [profitAlertThreshold, setProfitAlertThreshold] = useState(defaultState.profitAlertThreshold);
  const [profitAlertEnabled, setProfitAlertEnabled] = useState(defaultState.profitAlertEnabled);

  // UI State
  const [selectedUserId, setSelectedUserId] = useState<string | null>(defaultState.selectedUserId);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(defaultState.selectedOrderId);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(defaultState.expandedSections);

  // Modal State
  const [isUserDetailModalOpen, setIsUserDetailModalOpen] = useState(defaultState.isUserDetailModalOpen);
  const [isOrderDetailModalOpen, setIsOrderDetailModalOpen] = useState(defaultState.isOrderDetailModalOpen);
  const [isAuditDetailModalOpen, setIsAuditDetailModalOpen] = useState(defaultState.isAuditDetailModalOpen);

  // Notifications
  const [showNotifications, setShowNotifications] = useState(defaultState.showNotifications);
  const [notificationSound, setNotificationSound] = useState(defaultState.notificationSound);

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  }, []);

  const resetAllState = useCallback(() => {
    setDashboardRefreshInterval(defaultState.dashboardRefreshInterval);
    setUsersPageSize(defaultState.usersPageSize);
    setUsersSearchQuery(defaultState.usersSearchQuery);
    setUsersCurrentPage(defaultState.usersCurrentPage);
    setUsersSortBy(defaultState.usersSortBy);
    setOrdersPageSize(defaultState.ordersPageSize);
    setOrdersSearchQuery(defaultState.ordersSearchQuery);
    setOrdersCurrentPage(defaultState.ordersCurrentPage);
    setOrdersStatusFilter(defaultState.ordersStatusFilter);
    setOrdersSortBy(defaultState.ordersSortBy);
    setAuditLogsPageSize(defaultState.auditLogsPageSize);
    setAuditLogsSearchQuery(defaultState.auditLogsSearchQuery);
    setAuditLogsCurrentPage(defaultState.auditLogsCurrentPage);
    setAuditLogsActionFilter(defaultState.auditLogsActionFilter);
    setAuditLogsSortBy(defaultState.auditLogsSortBy);
    setProfitDateRange(defaultState.profitDateRange);
    setProfitGroupBy(defaultState.profitGroupBy);
    setPeakExportFrequency(defaultState.peakExportFrequency);
    setPeakExportAutoSync(defaultState.peakExportAutoSync);
    setProfitAlertThreshold(defaultState.profitAlertThreshold);
    setProfitAlertEnabled(defaultState.profitAlertEnabled);
    setSelectedUserId(defaultState.selectedUserId);
    setSelectedOrderId(defaultState.selectedOrderId);
    setExpandedSections(defaultState.expandedSections);
    setIsUserDetailModalOpen(defaultState.isUserDetailModalOpen);
    setIsOrderDetailModalOpen(defaultState.isOrderDetailModalOpen);
    setIsAuditDetailModalOpen(defaultState.isAuditDetailModalOpen);
    setShowNotifications(defaultState.showNotifications);
    setNotificationSound(defaultState.notificationSound);
  }, []);

  const value: AdminContextType = {
    dashboardRefreshInterval,
    setDashboardRefreshInterval,
    usersPageSize,
    setUsersPageSize,
    usersSearchQuery,
    setUsersSearchQuery,
    usersCurrentPage,
    setUsersCurrentPage,
    usersSortBy,
    setUsersSortBy,
    ordersPageSize,
    setOrdersPageSize,
    ordersSearchQuery,
    setOrdersSearchQuery,
    ordersCurrentPage,
    setOrdersCurrentPage,
    ordersStatusFilter,
    setOrdersStatusFilter,
    ordersSortBy,
    setOrdersSortBy,
    auditLogsPageSize,
    setAuditLogsPageSize,
    auditLogsSearchQuery,
    setAuditLogsSearchQuery,
    auditLogsCurrentPage,
    setAuditLogsCurrentPage,
    auditLogsActionFilter,
    setAuditLogsActionFilter,
    auditLogsSortBy,
    setAuditLogsSortBy,
    profitDateRange,
    setProfitDateRange,
    profitGroupBy,
    setProfitGroupBy,
    peakExportFrequency,
    setPeakExportFrequency,
    peakExportAutoSync,
    setPeakExportAutoSync,
    profitAlertThreshold,
    setProfitAlertThreshold,
    profitAlertEnabled,
    setProfitAlertEnabled,
    selectedUserId,
    setSelectedUserId,
    selectedOrderId,
    setSelectedOrderId,
    expandedSections,
    toggleSection,
    isUserDetailModalOpen,
    setIsUserDetailModalOpen,
    isOrderDetailModalOpen,
    setIsOrderDetailModalOpen,
    isAuditDetailModalOpen,
    setIsAuditDetailModalOpen,
    showNotifications,
    setShowNotifications,
    notificationSound,
    setNotificationSound,
    resetAllState,
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}

/**
 * Hook to use AdminContext
 * Only use this hook in admin routes (/admin/*)
 */
export function useAdmin(): AdminContextType {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
}
